import { Info as InfoIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { DetailedFacility } from "../../../../types/facilityTypes";
import { getDisplayNameFromDbValue } from "../../../../utils/facilityStatusMapping";
import ConfidenceIndicator from "../../../common/ConfidenceIndicator";

interface FacilityDetailProps {
  detailedFacility: DetailedFacility;
  onEntityClick?: (entityId: string) => void;
}

const FacilityDetail: React.FC<FacilityDetailProps> = ({
  detailedFacility,
  onEntityClick,
}) => {
  return (
    <Box sx={{ p: 0, flexShrink: 0 }}>
      <Grid container spacing={3}>
        {/* Facility Overview Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "background.paper",
              p: 3,
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              mb: 1,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "primary.main", mb: 3 }}
            >
              Facility Overview
            </Typography>
            <Grid container spacing={2}>
              {/* <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Industry
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {}
                    </Typography>
                  </Grid> */}
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedFacility?.f?.country_iso2 || "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Employees
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedFacility?.f?.number_of_employees?.toLocaleString() ||
                    "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Operational Since
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedFacility?.f?.operational_from || "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedFacility?.f?.address}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Coordinates
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedFacility?.f?.latitude &&
                  detailedFacility?.f?.longitude
                    ? `${detailedFacility.f.latitude.toFixed(4)}, ${detailedFacility.f.longitude.toFixed(4)}`
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Confidence Level
                  </Typography>
                  <Tooltip
                    title="The confidence level is calculated based on the number of sources we have for this entity and how recent the data is. More sources and fresher data result in higher confidence."
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      sx={{
                        padding: 0,
                        width: 16,
                        height: 16,
                        "& .MuiSvgIcon-root": { fontSize: 14 },
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ mt: 0.5 }}>
                  {detailedFacility?.f?.confidence_level !== null &&
                  detailedFacility?.f?.confidence_level !== undefined ? (
                    <ConfidenceIndicator
                      confidence={detailedFacility.f.confidence_level}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      -
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" color="text.secondary">
                  Data Sources
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {detailedFacility?.f?.n_sources || "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Operational Status Section */}
        {((detailedFacility?.f?.status_labels?.length || 0) > 0 ||
          detailedFacility?.f?.status_summary ||
          detailedFacility?.f?.status_last_update_date) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Operational Status
              </Typography>

              {/* Status Information - Inline Layout */}
              <Grid container spacing={2}>
                {/* Status Summary - 2/4 of the grid */}
                {detailedFacility?.f?.status_summary && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status Summary:
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {detailedFacility.f.status_summary}
                    </Typography>
                  </Grid>
                )}

                {/* Status Labels - 1/4 of the grid */}
                {detailedFacility?.f?.status_labels &&
                  detailedFacility.f.status_labels.length > 0 && (
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        Current Status:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {detailedFacility.f.status_labels.map(
                          (status, index) => {
                            const displayName = (() => {
                              try {
                                return getDisplayNameFromDbValue(status);
                              } catch {
                                return status;
                              }
                            })();
                            return (
                              <Chip
                                key={index}
                                label={displayName}
                                color={
                                  status === "active" || status === "opened"
                                    ? "success"
                                    : status === "closed" ||
                                        status === "cancelled" ||
                                        status === "demolished"
                                      ? "error"
                                      : status === "planned" ||
                                          status === "under_construction" ||
                                          status === "expansion_planned" ||
                                          status === "expanding" ||
                                          status === "at_risk"
                                        ? "warning"
                                        : status === "paused" ||
                                            status === "capacity_reduction" ||
                                            status ===
                                              "capacity_reduction_planned"
                                          ? "info"
                                          : "default"
                                }
                                variant="outlined"
                                size="small"
                              />
                            );
                          }
                        )}
                      </Box>
                    </Grid>
                  )}

                {/* Last Updated Date - 1/4 of the grid */}
                {detailedFacility?.f?.status_last_update_date && (
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {detailedFacility.f.status_last_update_date}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )}

        {/* Ownership & Control Section */}
        {(() => {
          // Filter organizations for ownership relationships - same logic as Organizations tab
          const validOwners =
            detailedFacility?.organizations?.filter(
              (item) => item.organization && item.organization.id
            ) || [];

          // Further filter for ownership relationships
          const ownerOrgs = validOwners.filter(
            (item) =>
              (item.relationship?.semantic_role === "Owner" ||
                item.relationship?.semantic_role === "Owner (JV)" ||
                item.relationship?.semantic_role === "Owner (Majority)" ||
                item.relationship?.semantic_role === "Owner (Partial)") &&
              item.relationship?.relation_status === "active"
          );

          // Fallback: if no specific owners found, show first few organizations as potential owners
          const displayOrgs =
            ownerOrgs.length > 0 ? ownerOrgs : validOwners.slice(0, 3);

          return displayOrgs.length > 0 ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  p: 3,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "primary.main", mb: 3 }}
                >
                  Ownership & Control
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {displayOrgs.slice(0, 4).map((ownerItem, index) => {
                    const ownershipPercentage =
                      ownerItem.relationship?.ownership_share;
                    const ownerName =
                      ownerItem.organization?.name || "Unknown Owner";

                    return (
                      <Chip
                        key={index}
                        label={
                          ownershipPercentage &&
                          typeof ownershipPercentage === "number"
                            ? `${ownerName} (${(() => {
                                try {
                                  const share = ownershipPercentage;
                                  if (share <= 1.0) {
                                    return `${(share * 100).toFixed(1)}%`;
                                  } else {
                                    return `${share.toFixed(1)}%`;
                                  }
                                } catch (e) {
                                  return String(ownershipPercentage);
                                }
                              })()})`
                            : ownerName
                        }
                        onClick={() =>
                          onEntityClick?.(ownerItem.organization?.id || "")
                        }
                        onMouseDown={(event) => {
                          if (event.button === 1) {
                            event.preventDefault();
                            window.open(
                              `/entity/${ownerItem.organization?.id}`,
                              "_blank"
                            );
                          }
                        }}
                        variant="outlined"
                        size="small"
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            borderColor: "white",
                          },
                        }}
                      />
                    );
                  })}

                  {displayOrgs.length > 4 && (
                    <Tooltip
                      title={
                        <Box>
                          {displayOrgs.slice(4).map((org, idx) => (
                            <div key={idx}>{org.organization.name}</div>
                          ))}
                        </Box>
                      }
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ cursor: "help" }}
                      >
                        +{displayOrgs.length - 4} more
                      </Typography>
                    </Tooltip>
                  )}

                  {/* <Button
                        size="small"
                        onClick={() => setActiveTab(0)} // Switch to Organizations tab
                        sx={{ ml: 1, textTransform: 'none' }}
                      >
                        View All Relationships →
                      </Button> */}
                </Box>
              </Box>
            </Grid>
          ) : null;
        })()}

        {/* Operations & Capabilities Section */}
        {((detailedFacility?.f?.services?.length || 0) > 0 ||
          (detailedFacility?.f?.products_produced?.length || 0) > 0 ||
          (detailedFacility?.f?.technologies_used?.length || 0) > 0) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Operations & Capabilities
              </Typography>

              {/* Services */}
              {detailedFacility?.f?.services &&
                detailedFacility.f.services.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Services Offered:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedFacility.f.services
                        .slice(0, 6)
                        .map((service, index) => (
                          <Chip
                            key={index}
                            label={service}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedFacility.f.services.length > 6 && (
                        <Tooltip
                          title={
                            <Box>
                              {detailedFacility.f.services
                                .slice(6)
                                .map((service, idx) => (
                                  <div key={idx}>{service}</div>
                                ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
                          >
                            +{detailedFacility.f.services.length - 6} more
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}

              {/* Products */}
              {detailedFacility?.f?.products_produced &&
                detailedFacility.f.products_produced.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Products Produced:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedFacility.f.products_produced
                        .slice(0, 6)
                        .map((product, index) => (
                          <Chip
                            key={index}
                            label={product}
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedFacility.f.products_produced.length > 6 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1, alignSelf: "center" }}
                        >
                          +{detailedFacility.f.products_produced.length - 6}{" "}
                          more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

              {/* Technologies */}
              {detailedFacility?.f?.technologies_used &&
                detailedFacility.f.technologies_used.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      Technologies Used:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedFacility.f.technologies_used
                        .slice(0, 6)
                        .map((tech, index) => (
                          <Chip
                            key={index}
                            label={tech}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedFacility.f.technologies_used.length > 6 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 1, alignSelf: "center" }}
                        >
                          +{detailedFacility.f.technologies_used.length - 6}{" "}
                          more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
            </Box>
          </Grid>
        )}

        {/* Performance Metrics Section */}
        {detailedFacility?.f?.production_kpis &&
          detailedFacility.f.production_kpis.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  p: 3,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "primary.main", mb: 3 }}
                >
                  Performance Metrics
                </Typography>
                <Grid container spacing={2}>
                  {detailedFacility.f.production_kpis
                    .slice(0, 4)
                    .map((kpi, index) => (
                      <Grid item xs={6} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid #444",
                            borderRadius: 1,
                            backgroundColor: "#1a1a1a",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {kpi.product_or_service}
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary.main"
                            gutterBottom
                          >
                            {kpi.capacity_value?.toLocaleString()}{" "}
                            {kpi.capacity_unit}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            gutterBottom
                          >
                            {kpi.reference_period}
                          </Typography>
                          {kpi.summary && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 1,
                                fontSize: "0.75rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {kpi.summary}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </Grid>
          )}

        {/* Industry Classification Section */}
        {((detailedFacility?.f?.naics_codes_with_title?.length ?? 0) > 0 ||
          (detailedFacility?.f?.input_hs_codes_with_title?.length ?? 0) > 0 ||
          (detailedFacility?.f?.output_hs_codes_with_title?.length ?? 0) >
            0) && (
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Industry Classification
              </Typography>
              {/* NAICS Codes */}
              {detailedFacility?.f?.naics_codes_with_title &&
                detailedFacility.f.naics_codes_with_title.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      NAICS Industry Codes:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {detailedFacility.f.naics_codes_with_title
                        .slice(0, 6)
                        .map((code, index) => (
                          <Chip
                            key={index}
                            label={code}
                            color="info"
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      {detailedFacility.f.naics_codes_with_title.length > 6 && (
                        <Tooltip
                          title={
                            <Box>
                              {detailedFacility.f.naics_codes_with_title
                                .slice(6)
                                .map((code, idx) => (
                                  <div key={idx}>{code}</div>
                                ))}
                            </Box>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
                          >
                            +
                            {detailedFacility.f.naics_codes_with_title.length -
                              6}{" "}
                            more
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                )}

              {/* HS Codes */}
              <Grid container spacing={3}>
                {detailedFacility?.f?.input_hs_codes_with_title &&
                  detailedFacility.f.input_hs_codes_with_title.length > 0 && (
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Input HS Codes:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {detailedFacility.f.input_hs_codes_with_title
                          .slice(0, 4)
                          .map((code, index) => (
                            <Chip
                              key={index}
                              label={code}
                              color="warning"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        {detailedFacility.f.input_hs_codes_with_title.length >
                          4 && (
                          <Tooltip
                            title={
                              <Box>
                                {detailedFacility.f.input_hs_codes_with_title
                                  .slice(4)
                                  .map((code, idx) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 1,
                                alignSelf: "center",
                                cursor: "help",
                              }}
                            >
                              +
                              {detailedFacility.f.input_hs_codes_with_title
                                .length - 4}{" "}
                              more
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  )}
                {detailedFacility?.f?.output_hs_codes_with_title &&
                  detailedFacility.f.output_hs_codes_with_title.length > 0 && (
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Output HS Codes:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {detailedFacility.f.output_hs_codes_with_title
                          .slice(0, 4)
                          .map((code, index) => (
                            <Chip
                              key={index}
                              label={code}
                              color="success"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        {detailedFacility.f.output_hs_codes_with_title.length >
                          4 && (
                          <Tooltip
                            title={
                              <Box>
                                {detailedFacility.f.output_hs_codes_with_title
                                  .slice(4)
                                  .map((code, idx) => (
                                    <div key={idx}>{code}</div>
                                  ))}
                              </Box>
                            }
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 1,
                                alignSelf: "center",
                                cursor: "help",
                              }}
                            >
                              +
                              {detailedFacility.f.output_hs_codes_with_title
                                .length - 4}{" "}
                              more
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FacilityDetail;
